// ===========================================================================
// frontend/src/screens/CropListing/Pricing.jsx
// NEW UI — Pricing Step (Step 2 of 4)
// Reference: Figma design — "Set your price and available quantity"
// ===========================================================================

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Divider,
    Typography,
    TextField,
    Grid,
    Button,
    Switch,
    InputAdornment,
    useTheme,
    useMediaQuery,
    FormControlLabel,
    Radio,
    RadioGroup,
} from '@mui/material';
import {
    IconCheck,
    IconCurrencyRupee,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { colors, fonts } from '../../ColorFont/color';
import NewHeaderNavWithSidebar from '../../components/HeaderNav/NewHeaderNavWithSidebar';

// ─── Constants ───────────────────────────────────────────────────────────────
const PRIMARY = colors.primary;
const PRIMARY_DARK = colors.primary_dark || '#149343';

// ─── Translations ─────────────────────────────────────────────────────────────
const pricingTranslations = {
    en: {
        pageTitle: 'Add New Crop',
        breadcrumb: { dashboard: 'Dashboard', cropListings: 'Crop Listings', createNew: 'Create New' },
        steps: {
            cropCategory: 'Crop Category',
            varietyGrade: 'Variety & Grade', 
            pricing: 'Pricing',
            photos: 'Photos',
            locationDelivery: 'Location & Delivery',
        },
        cardTitle: 'Set your price and available quantity',
        cardSubtitle: 'Enter clear pricing and stock details so buyers can make faster decisions.',
        pricePerUnit: 'Price',
        areaUnit: 'Area Unit',
        priceUnit: 'Price Unit',
        quintal: 'Quintal',
        kg: 'Kg',
        ton: 'Ton',
        acre: 'Acre',
        hectare: 'Hectare',
        guntha: 'Guntha',
        quantityAvailable: 'Quantity / Area *',
        enterQuantity: 'Enter quantity or area',
        pricePlaceholder: 'Enter price',
        negotiable: 'Negotiable',
        customUnit: 'Custom Unit Name',
        customPlaceholder: 'e.g., Boxes, Pieces',
        back: 'Back',
        next: 'Next',
        custom: 'Custom',
    },
    hi: {
        pageTitle: 'नई फसल जोड़ें',
        breadcrumb: { dashboard: 'डैशबोर्ड', cropListings: 'फसल सूची', createNew: 'नया बनाएं' },
        steps: {
            cropCategory: 'फसल श्रेणी',
            varietyGrade: 'किस्म और ग्रेड',
            pricing: 'मूल्य',
            photos: 'फोटो',
            locationDelivery: 'स्थान और डिलीवरी',
        },
        cardTitle: 'अपनी कीमत और उपलब्ध मात्रा निर्धारित करें',
        cardSubtitle: 'स्पष्ट मूल्य और स्टॉक विवरण दर्ज करें ताकि खरीदार तेज़ निर्णय ले सकें।',
        pricePerUnit: 'मूल्य',
        areaUnit: 'क्षेत्र इकाई',
        priceUnit: 'मूल्य इकाई',
        quintal: 'क्विंटल',
        kg: 'किलो',
        ton: 'टन',
        acre: 'एकड़',
        hectare: 'हेक्टेयर',
        guntha: 'गुंठा',
        quantityAvailable: 'मात्रा / क्षेत्र *',
        enterQuantity: 'मात्रा किंवा क्षेत्र प्रविष्ट करा',
        pricePlaceholder: 'कीमत दर्ज करें',
        negotiable: 'बातचीत योग्य',
        customUnit: 'कस्टम इकाई नाम',
        customPlaceholder: 'जैसे, बक्से, टुकड़े',
        back: 'वापस',
        next: 'अगला',
        custom: 'कस्टम',
    },
    mr: {
        pageTitle: 'नवीन पीक जोडा',
        breadcrumb: { dashboard: 'डॅशबोर्ड', cropListings: 'पीक सूची', createNew: 'नवीन तयार करा' },
        steps: {
            cropCategory: 'पीक श्रेणी',
            varietyGrade: 'वाण आणि दर्जा',
            pricing: 'किंमत',
            photos: 'फोटो',
            locationDelivery: 'स्थान आणि डिलिव्हरी',
        },
        cardTitle: 'आपली किंमत आणि उपलब्ध प्रमाण सेट करा',
        cardSubtitle: 'स्पष्ट किंमत आणि साठा तपशील प्रविष्ट करा जेणेकरून खरेदीदार जलद निर्णय घेऊ शकतील.',
        pricePerUnit: 'किंमत',
        areaUnit: 'क्षेत्र युनिट',
        priceUnit: 'किंमत युनिट',
        quintal: 'क्विंटल',
        kg: 'किलो',
        ton: 'टन',
        acre: 'एकर',
        hectare: 'हेक्टर',
        guntha: 'गुंठा',
        quantityAvailable: 'प्रमाण / क्षेत्र *',
        enterQuantity: 'प्रमाण किंवा क्षेत्र प्रविष्ट करा',
        pricePlaceholder: 'किंमत प्रविष्ट करा',
        negotiable: 'वाटाघाटी योग्य',
        customUnit: 'कस्टम युनिट नाव',
        customPlaceholder: 'उदा., बॉक्स, तुकडे',
        back: 'मागे',
        next: 'पुढे',
        custom: 'कस्टम',
    },
};

// ─── Stepper Component ────────────────────────────────────────────────────────
const stepperData = (tLocal) => [
    { label: tLocal.steps.cropCategory, state: 'done', step: 1 },
    { label: tLocal.steps.varietyGrade, state: 'done', step: 2 },
    { label: tLocal.steps.pricing, state: 'active', step: 3 },
    { label: tLocal.steps.photos, state: 'inactive', step: 4 },
    { label: tLocal.steps.locationDelivery, state: 'inactive', step: 5 },
];

const Stepper = ({ steps, isMobile }) => {
    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            width: '100%',
            mb: { xs: 3, md: 5 },
            position: 'relative',
        }}>
            {/* Background connector line */}
            <Box sx={{
                position: 'absolute',
                top: 18,
                left: '10%',
                right: '10%',
                height: '2px',
                backgroundColor: '#E0E5EB',
                zIndex: 0,
            }} />

            {steps.map((step, idx) => {
                const isDone = step.state === 'done';
                const isActive = step.state === 'active';
                const isInactive = step.state === 'inactive';

                // Progress line fill up to active step
                const filledWidth = isDone ? '100%' : isActive ? '50%' : '0%';
                const showFill = isDone;

                return (
                    <Box
                        key={idx}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            flex: 1,
                            position: 'relative',
                            zIndex: 1,
                        }}
                    >
                        {/* Circle */}
                        <Box sx={{
                            width: { xs: 32, md: 36 },
                            height: { xs: 32, md: 36 },
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: isDone ? PRIMARY : isActive ? PRIMARY : '#E0E5EB',
                            border: isActive ? `3px solid ${PRIMARY}` : isInactive ? '2px solid #C4CDD5' : 'none',
                            boxShadow: isActive ? `0 0 0 4px ${PRIMARY}22` : 'none',
                            transition: 'all 0.3s ease',
                        }}>
                            {isDone ? (
                                <IconCheck size={18} color="#FFFFFF" stroke={3} />
                            ) : (
                                <Typography sx={{
                                    fontFamily: fonts.poppins,
                                    fontWeight: 700,
                                    fontSize: { xs: '13px', md: '14px' },
                                    color: isActive ? '#FFFFFF' : '#919EAB',
                                }}>
                                    {step.step}
                                </Typography>
                            )}
                        </Box>

                        {/* Label */}
                        <Typography sx={{
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: isActive ? 700 : isDone ? 600 : 500,
                            fontSize: { xs: '11px', md: '13px' },
                            color: isActive ? '#1C252E' : isDone ? '#1C252E' : '#919EAB',
                            mt: 1,
                            textAlign: 'center',
                            maxWidth: { xs: '72px', sm: '100px' },
                            lineHeight: 1.3,
                        }}>
                            {step.label}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const Pricing = () => {
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const langCode = i18n.language || 'en';
    const currentLang = langCode.startsWith('hi') ? 'hi' : (langCode.startsWith('mr') || langCode.startsWith('mar')) ? 'mr' : 'en';
    const tLocal = pricingTranslations[currentLang];

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // ─── Form State ────────────────────────────────────────────────────────────
    const location = useLocation();
    const existingData = location.state?.cropData || {};
    
    const [pricePerUnit, setPricePerUnit] = useState(existingData.amount || '');
    const [areaValue, setAreaValue] = useState(existingData.area || '');
    const [areaUnit, setAreaUnit] = useState(existingData.areaUnit || 'Acre');
    const [customAreaUnitName, setCustomAreaUnitName] = useState(existingData.customAreaUnit || '');
    const [priceUnit, setPriceUnit] = useState(existingData.priceUnit || 'Kg');
    const [negotiable, setNegotiable] = useState(existingData.isNegotiable || false);
    const [errors, setErrors] = useState({});

    // Refs for scrolling to errors
    const priceRef = React.useRef(null);
    const areaRef = React.useRef(null);

    const scrollToFirstError = (newErrors) => {
        if (newErrors.area) {
            areaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            areaRef.current?.focus();
        } else if (newErrors.customUnit) {
            // Focus custom unit field if needed
        } else if (newErrors.price) {
            priceRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            priceRef.current?.focus();
        }
    };

    const steps = stepperData(tLocal);

    // Validation messages
    const valMsgs = {
        en: 'This field is required',
        hi: 'यह फ़ील्ड अनिवार्य है',
        mr: 'हे क्षेत्र अनिवार्य आहे'
    };
    const langCodeVal = i18n.language || 'en';
    const currentLangVal = langCodeVal.startsWith('hi') ? 'hi' : (langCodeVal.startsWith('mr') || langCodeVal.startsWith('mar')) ? 'mr' : 'en';
    const tVal = valMsgs[currentLangVal] || valMsgs.en;

    const handleNext = () => {
        const newErrors = {};
        if (!pricePerUnit) newErrors.price = true;
        if (!areaValue) newErrors.area = true;
        if (areaUnit === 'Custom' && !customAreaUnitName) newErrors.customUnit = true;
        
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            scrollToFirstError(newErrors);
            return;
        }

        const pricingData = {
            amount: pricePerUnit,
            priceUnit: priceUnit,
            area: areaValue,
            areaUnit: areaUnit,
            customAreaUnit: areaUnit === 'Custom' ? customAreaUnitName : '',
            isNegotiable: negotiable,
        };
        const updatedCropData = {
            ...(location.state?.cropData || {}),
            ...pricingData,
        };
        navigate('/photos', { 
            state: { 
                cropData: updatedCropData,
                isEdit: location.state?.isEdit,
                cropId: location.state?.cropId
            } 
        }); 
    };

    const handleBack = () => {
        const pricingData = {
            amount: pricePerUnit,
            priceUnit: priceUnit,
            area: areaValue,
            areaUnit: areaUnit,
            customAreaUnit: areaUnit === 'Custom' ? customAreaUnitName : '',
            isNegotiable: negotiable,
        };
        const updatedCropData = {
            ...(location.state?.cropData || {}),
            ...pricingData,
        };
        navigate('/add-crop-step-1', { 
            state: { 
                cropData: updatedCropData,
                isEdit: location.state?.isEdit,
                cropId: location.state?.cropId
            } 
        });
    };

    // ─── Shared TextField theme override ───────────────────────────────────────
    const fieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            fontFamily: fonts.publicSans || fonts.poppins,
            fontSize: '15px',
            backgroundColor: '#FFFFFF',
            '& fieldset': { borderColor: '#E0E5EB' },
            '&:hover fieldset': { borderColor: '#919EAB' },
            '&.Mui-focused fieldset': { borderColor: PRIMARY, borderWidth: '1.5px' },
            '&.Mui-error fieldset': { borderColor: '#FF4842' },
        },
        '& .MuiInputLabel-root': {
            fontFamily: fonts.publicSans || fonts.poppins,
            fontSize: '14px',
            color: '#637381',
        },
        '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY },
        '& .MuiInputLabel-root.Mui-error': { color: '#FF4842' },
        '& .MuiFormHelperText-root': {
            fontFamily: fonts.publicSans || fonts.poppins,
            fontSize: '12px',
            fontWeight: 500,
            ml: 1,
        }
    };

    return (
        <Box sx={{
            minHeight: '100%',
            backgroundColor: '#FFFFFF',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        }}>
                {/* ── Page Title + Breadcrumb ── */}
                <Box sx={{
                    width: '100%',
                    maxWidth: '900px',
                    px: 0,
                    pt: { xs: 3, md: 4 },
                    pb: { xs: 1, md: 2 },
                }}>
                    <Typography sx={{
                        fontFamily: fonts.poppins,
                        fontWeight: 800,
                        fontSize: { xs: '22px', md: '28px' },
                        color: '#1C252E',
                        mb: 1,
                    }}>
                        {location.state?.isEdit ? (currentLang === 'hi' ? 'फसल संपादित करें' : currentLang === 'mr' ? 'पीक संपादित करा' : 'Edit Crop') : tLocal.pageTitle}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 3, md: 4 } }}>
                        {[tLocal.breadcrumb.dashboard, tLocal.breadcrumb.cropListings, location.state?.isEdit ? (currentLang === 'hi' ? 'संपादित करें' : currentLang === 'mr' ? 'संपादित करा' : 'Edit Listing') : tLocal.breadcrumb.createNew].map((crumb, idx, arr) => (
                            <React.Fragment key={idx}>
                                <Typography sx={{
                                    fontFamily: fonts.publicSans || fonts.poppins,
                                    fontSize: '13px',
                                    fontWeight: idx === arr.length - 1 ? 600 : 400,
                                    color: idx === arr.length - 1 ? '#1C252E' : '#637381',
                                    cursor: idx < arr.length - 1 ? 'pointer' : 'default',
                                }}>
                                    {crumb}
                                </Typography>
                                {idx < arr.length - 1 && (
                                    <Box sx={{
                                        width: '5px', height: '5px', borderRadius: '50%',
                                        backgroundColor: '#637381'
                                    }} />
                                )}
                            </React.Fragment>
                        ))}
                    </Box>

                    {/* ── Stepper ── */}
                    <Stepper steps={steps} isMobile={isMobile} />
                </Box>

                {/* ── Main Card ── */}
                <Box sx={{
                    width: '100%',
                    maxWidth: '900px',
                    px: 0,
                    pb: { xs: 4, md: 6 },
                }}>
                    <Box sx={{
                        backgroundColor: '#FFFFFF',
                        borderRadius: { xs: '16px', md: '20px' },
                        border: '1px solid #E0E5EB',
                        boxShadow: '0px 4px 24px rgba(0,0,0,0.04)',
                        px: { xs: 3, md: 6 },
                        py: { xs: 4, md: 5 },
                    }}>
                        {/* Card heading */}
                        <Typography sx={{
                            fontFamily: fonts.poppins,
                            fontWeight: 800,
                            fontSize: { xs: '20px', md: '24px' },
                            color: '#1C252E',
                            textAlign: 'center',
                            mb: 1,
                        }}>
                            {tLocal.cardTitle}
                        </Typography>
                        <Typography sx={{
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontSize: { xs: '13px', md: '14px' },
                            color: '#637381',
                            textAlign: 'center',
                            mb: { xs: 3, md: 4 },
                            maxWidth: '480px',
                            mx: 'auto',
                            lineHeight: 1.6,
                        }}>
                            {tLocal.cardSubtitle}
                        </Typography>

                        {/* ── Area Details ── */}
                        <Box sx={{ mb: 4 }}>
                            <Grid container spacing={3} alignItems="flex-end">
                                <Grid item xs={12} md={7}>
                                    <TextField
                                        fullWidth
                                        inputRef={areaRef}
                                        label={tLocal.quantityAvailable}
                                        placeholder={tLocal.enterQuantity}
                                        value={areaValue}
                                        onChange={(e) => {
                                            setAreaValue(e.target.value);
                                            if (errors.area) setErrors(prev => ({ ...prev, area: false }));
                                        }}
                                        error={!!errors.area}
                                        helperText={errors.area ? tVal : ""}
                                        type="number"
                                        sx={fieldSx}
                                    />
                                </Grid>
                                <Grid item xs={12} md={5}>
                                    <RadioGroup
                                        row
                                        value={areaUnit}
                                        onChange={(e) => setAreaUnit(e.target.value)}
                                        sx={{ gap: 1 }}
                                    >
                                        {['Acre', 'Hectare', 'Guntha', 'Kg', 'Quintal', 'Ton', 'Custom'].map((unit) => (
                                            <FormControlLabel
                                                key={unit}
                                                value={unit}
                                                control={<Radio sx={{ color: '#C4CDD5', '&.Mui-checked': { color: PRIMARY }, padding: '4px' }} />}
                                                label={<Typography sx={{ fontFamily: fonts.publicSans, fontSize: '13px', fontWeight: areaUnit === unit ? 700 : 500 }}>{tLocal[unit.toLowerCase()] || unit}</Typography>}
                                            />
                                        ))}
                                    </RadioGroup>
                                </Grid>
                            </Grid>
                            
                            {/* Custom Area Unit Input */}
                            {areaUnit === 'Custom' && (
                                <Box sx={{ mt: 2, maxWidth: '400px' }}>
                                    <TextField
                                        fullWidth
                                        label={tLocal.customUnit + " *"}
                                        placeholder={tLocal.customPlaceholder}
                                        value={customAreaUnitName}
                                        onChange={(e) => {
                                            setCustomAreaUnitName(e.target.value);
                                            if (errors.customUnit) setErrors(prev => ({ ...prev, customUnit: false }));
                                        }}
                                        error={!!errors.customUnit}
                                        helperText={errors.customUnit ? tVal : ""}
                                        sx={fieldSx}
                                    />
                                </Box>
                            )}
                        </Box>

                        {/* ── Price Details ── */}
                        <Box sx={{ mb: 4 }}>
                            <Grid container spacing={3} alignItems="flex-end">
                                <Grid item xs={12} md={7}>
                                    <TextField
                                        fullWidth
                                        inputRef={priceRef}
                                        label={tLocal.pricePerUnit + " *"}
                                        placeholder={tLocal.pricePlaceholder}
                                        value={pricePerUnit}
                                        onChange={(e) => {
                                            setPricePerUnit(e.target.value);
                                            if (errors.price) setErrors(prev => ({ ...prev, price: false }));
                                        }}
                                        error={!!errors.price}
                                        helperText={errors.price ? tVal : ""}
                                        type="number"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <IconCurrencyRupee size={18} color={errors.price ? "#FF4842" : "#637381"} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={fieldSx}
                                    />
                                </Grid>
                                <Grid item xs={12} md={5}>
                                    <RadioGroup
                                        row
                                        value={priceUnit}
                                        onChange={(e) => setPriceUnit(e.target.value)}
                                        sx={{ gap: 1 }}
                                    >
                                        {['Kg', 'Quintal', 'Ton'].map((unit) => (
                                            <FormControlLabel
                                                key={unit}
                                                value={unit}
                                                control={<Radio sx={{ color: '#C4CDD5', '&.Mui-checked': { color: PRIMARY }, padding: '4px' }} />}
                                                label={<Typography sx={{ fontFamily: fonts.publicSans, fontSize: '13px', fontWeight: priceUnit === unit ? 700 : 500 }}>{tLocal[unit.toLowerCase()]}</Typography>}
                                            />
                                        ))}
                                    </RadioGroup>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* ── Negotiable Toggle ── */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: { xs: 4, md: 5 },
                        }}>
                            <Typography sx={{
                                fontFamily: fonts.publicSans || fonts.poppins,
                                fontWeight: 700,
                                fontSize: '15px',
                                color: '#1C252E',
                            }}>
                                {tLocal.negotiable}
                            </Typography>
                            <Switch
                                checked={negotiable}
                                onChange={(e) => setNegotiable(e.target.checked)}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: PRIMARY,
                                        '&:hover': { backgroundColor: `${PRIMARY}18` },
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: PRIMARY,
                                    },
                                    '& .MuiSwitch-track': {
                                        borderRadius: '12px',
                                        backgroundColor: '#C4CDD5',
                                    },
                                }}
                            />
                        </Box>

                        {/* ── Back + Next Buttons ── */}
                        <Box sx={{
                            display: 'flex',
                            gap: { xs: 2, md: 3 },
                            justifyContent: 'center',
                            flexDirection: { xs: 'column-reverse', sm: 'row' },
                        }}>
                            <Button
                                variant="outlined"
                                onClick={handleBack}
                                fullWidth={isMobile}
                                sx={{
                                    borderRadius: '50px',
                                    px: { xs: 3, md: 6 },
                                    height: '52px',
                                    fontFamily: fonts.publicSans || fonts.poppins,
                                    fontWeight: 700,
                                    fontSize: '15px',
                                    textTransform: 'none',
                                    color: '#1C252E',
                                    borderColor: '#DFE3E8',
                                    minWidth: { sm: '180px' },
                                    '&:hover': {
                                        borderColor: '#919EAB',
                                        backgroundColor: '#F4F6F8',
                                    },
                                }}
                            >
                                {tLocal.back}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleNext}
                                fullWidth={isMobile}
                                sx={{
                                    borderRadius: '50px',
                                    px: { xs: 3, md: 6 },
                                    height: '52px',
                                    fontFamily: fonts.publicSans || fonts.poppins,
                                    fontWeight: 700,
                                    fontSize: '15px',
                                    textTransform: 'none',
                                    backgroundColor: PRIMARY,
                                    color: '#FFFFFF',
                                    minWidth: { sm: '200px' },
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: PRIMARY_DARK,
                                        boxShadow: 'none',
                                    },
                                }}
                            >
                                {tLocal.next}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Box>
    );
};

export default Pricing;
